import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { ClearLines } from "components/EditPage/ClearLines";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { LineColumnFillHeading } from "components/formFields/line/LineColumnFillHeading";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { LineItemFooter } from "components/formFields/line/LineItemFooter";
import { DragHandleHeading } from "components/utility/DragHandle";
import { EditLineButtonHeading } from "components/utility/EditLineButton";

import { i18n } from "services/i18nService";
import { expandItemGroup } from "services/sosInventoryService/expandItemGroup";

import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { useLineItemHandlers } from "hooks/useLineItemHandlers";

import { MemoizedLineItem } from "views/FulfillmentTransactions/MemoizedLineItem";

import { EMPTY_LINE_ITEM } from "editConfig";

export function LineItems(props) {
  const {
    record,
    location,
    objectType,
    lines,
    items,
    totals,
    itemsCount,
    addItem,
    classes,
    classesCount,
    lineHandler,
    setClearLines,
    lineItemsErrors,
    quantityLabelCode,
    showClass,
  } = props;

  const jobWorkcenters = useJobWorkcenters();

  const {
    readOnlyLineIds,
    setLineEdit,
    handleDeleteLine,
    insertEmptyLine,
    handleDrop,
  } = useLineItemHandlers(
    record?.id,
    lines,
    lineHandler,
    EMPTY_LINE_ITEM[objectType]
  );

  // SETTINGS
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const showQuantityOnBuildsAndShipments = useSelector(
    (state) =>
      state.userCompanySettings.settings.showQuantityOnBuildsAndShipments
  );

  if (!lines) {
    return null;
  }

  return (
    <TableContainer data-testing="lineItems" sx={{ overflowX: "initial" }}>
      <Table size="small" padding="none" stickyHeader>
        <TableHead>
          <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
            <DragHandleHeading />
            <EditLineButtonHeading recordId={record.id} />
            <ClearLines setClearLines={setClearLines} />
            <LineTableHeadingCell>{i18n("columns.Line")}</LineTableHeadingCell>
            <LineInventoryItemHeading lineHandler={lineHandler} />
            <LineColumnHeading labelCode="Description" />
            {showQuantityOnBuildsAndShipments && (
              <LineColumnHeading labelCode="OnHand" align="right" />
            )}
            <LineColumnHeading labelCode={quantityLabelCode} />
            {uomEnabled && <LineColumnHeading labelCode="UOM" />}
            <LineColumnHeading labelCode="Weight" />
            <LineColumnHeading labelCode="Volume" />
            <LineColumnHeading labelCode="AppliedTo" />
            {classEnabled && showClass && (
              <LineColumnFillHeading
                labelCode="Class"
                field="class"
                lineHandler={lineHandler}
              />
            )}
            {jobEnabled && (
              <LineColumnFillHeading
                labelCode="Job"
                field="jobWorkcenter"
                lineHandler={lineHandler}
              />
            )}
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={handleDrop}>
          <Droppable droppableId="lineItems">
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {lines.map((line, index) => {
                  const lineReadOnly = readOnlyLineIds.has(index);

                  return (
                    <MemoizedLineItem
                      key={line.id || `temp-${index}`}
                      line={line}
                      index={index}
                      objectType={objectType}
                      record={record}
                      location={location}
                      lineHandler={lineHandler}
                      expandItemGroup={(line) =>
                        expandItemGroup(line, lineHandler, objectType, record)
                      }
                      items={items}
                      itemsCount={itemsCount}
                      addItem={addItem}
                      classes={classes}
                      classesCount={classesCount}
                      jobWorkcenters={jobWorkcenters}
                      lineItemsErrors={lineItemsErrors}
                      showClass={showClass}
                      insertEmptyLine={insertEmptyLine}
                      isLastLine={index === lines.length - 1}
                      setLineEdit={setLineEdit}
                      lineReadOnly={lineReadOnly}
                      nextLineReadOnly={readOnlyLineIds.has(index + 1)}
                      handleDeleteLine={handleDeleteLine}
                    />
                  );
                })}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
        <LineItemFooter
          showWeight
          showVolume
          totals={totals}
          firstCellSpan={5}
          lineHandler={lineHandler}
          objectType={objectType}
          recordId={record.id}
        />
      </Table>
    </TableContainer>
  );
}
